import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import { useChannel } from "@composables/useChannel";
import { useSurveyStore } from "@stores/survey/survey";
import type { AiGenerator, AiManyGenerator, AiResult } from "@/components/episto-ui/ai";
import { captureEvent } from "@/lib/analytics";

const animateTyping = (text: string, onChunk: (accumulated: string) => void, onDone: () => void): (() => void) => {
  if (!text) {
    onChunk("");
    onDone();
    return () => {};
  }
  let i = 0;
  const id = window.setInterval(() => {
    i = Math.min(i + 2, text.length);
    onChunk(text.slice(0, i));
    if (i >= text.length) {
      window.clearInterval(id);
      onDone();
    }
  }, 12);
  return () => window.clearInterval(id);
};

interface Pending {
  resolve: (results: AiResult[]) => void;
  fail: () => void;
}

interface WritingToolMessage {
  type?: string;
  body?: {
    request_id?: string;
    status?: "done" | "error";
    text?: string;
    results?: AiResult[];
  };
}

export const useAiWritingToolStore = defineStore("aiWritingTool", () => {
  const pending = new Map<string, Pending>();

  const handleReceivedMessage = (message: WritingToolMessage): void => {
    if (message?.type !== "writingTool") return;
    const { request_id: requestId, status, text, results } = message.body ?? {};
    if (!requestId) return;
    const entry = pending.get(requestId);
    if (!entry) return;

    if (status === "done") entry.resolve(results ?? [{ key: "content", content: text ?? "" }]);
    else if (status === "error") entry.fail();
  };

  const channel = useChannel<WritingToolMessage>("AiToolsChannel", {
    received: handleReceivedMessage,
  });

  const initialize = (): void => channel.subscribe();

  const generateMany: AiManyGenerator = (request, onResults) => {
    const requestId = crypto.randomUUID();
    let finished = false;

    const settle = (results: AiResult[]): void => {
      if (finished) return;
      finished = true;
      pending.delete(requestId);
      onResults(results);
    };

    pending.set(requestId, { resolve: settle, fail: () => settle([]) });

    useMyFetch("/ai_writing_tools")
      .post({
        client_id: getClientId(),
        request_id: requestId,
        survey_token: useSurveyStore().token ?? undefined,
        ai_writing_tool: request,
      })
      .then(({ error }) => {
        if (error?.value) {
          pending.get(requestId)?.fail();
          return;
        }

        captureEvent("ai_writing_tool_used", {
          action: request.action,
        });
      });

    return {
      cancel: () => {
        finished = true;
        pending.delete(requestId);
      },
    };
  };

  const generate: AiGenerator = (request, onChunk, onDone) => {
    let typingCancel: (() => void) | null = null;

    const handle = generateMany(
      {
        action: request.action,
        lang: request.lang,
        prompt: request.prompt,
        contents: [{ key: "content", content: request.originalText, format: request.format }],
      },
      (results) => {
        const text = results[0]?.content ?? "";
        if (request.format === "html") {
          onChunk(text);
          onDone();
        } else {
          typingCancel = animateTyping(text, onChunk, onDone);
        }
      },
    );

    return {
      cancel: () => {
        handle.cancel();
        typingCancel?.();
      },
    };
  };

  return { initialize, generate, generateMany };
});
